/**
 * DriverSafetyPage — `/driver-portal/safety`.
 *
 * Wired to:
 *   - POST /v1/driver-portal/me/safety-checkin    (pre-shift check-in form)
 *   - GET  /v1/driver-portal/me/safety-stats      (KPI tiles: SOS / open cases /
 *                                                  accident-free streak / due
 *                                                  attestations)
 *
 * The incident-case detail / distress-event feed panels remain as deferred
 * placeholders — service-delivery doesn't expose a per-driver case feed from
 * the web (panic detail flows live in the mobile driver app).
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  FileCheck,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import {
  getDriverPortalSafetyStats,
  postSafetyCheckin,
} from '../../lib/api/driver-portal';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { useDriverPortalContext } from './DriverPortalLayout';

export function DriverSafetyPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();
  const [vehicleOk, setVehicleOk] = useState(true);
  const [fitOk, setFitOk] = useState(true);
  const [notes, setNotes] = useState('');
  const [lastCheckin, setLastCheckin] = useState<string | null>(null);

  const safetyStats = useQuery({
    queryKey: ['driver-portal', 'safety-stats', driverId],
    queryFn: () => getDriverPortalSafetyStats(driverId),
    retry: false,
    staleTime: 60_000,
  });
  const stats = safetyStats.data ?? null;

  const checkin = useMutation({
    mutationFn: () =>
      postSafetyCheckin(
        { vehicleConditionOk: vehicleOk, fitToDriveOk: fitOk, notes: notes.trim() || undefined },
        // No driverIdOverride — let service resolve by JWT sub (keycloakSub match).
        undefined,
      ),
    onSuccess: (data) => {
      setLastCheckin(data.checkInAt);
      setNotes('');
    },
  });

  return (
    <div>
      <PageHeader
        title="Safety & cases"
        description="Pre-shift check-in, incident history, and compliance attestations."
        actions={
          <span className="inline-flex items-center gap-1.5 text-xs text-muted bg-gray-100 px-3 py-1.5 rounded-lg">
            <ShieldAlert className="w-3 h-3" /> Panic / SOS is mobile-only
          </span>
        }
      />

      {/* Pre-shift safety check-in — LIVE (C5) */}
      <div className="bg-white border border-border rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-nipost-green" /> Pre-shift safety check-in
        </h3>

        {lastCheckin ? (
          <div
            data-testid="checkin-success"
            className="flex items-center gap-3 bg-success-light border border-success rounded-lg p-4 mb-4"
          >
            <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-success">Check-in recorded</p>
              <p className="text-xs text-success/80">
                {new Date(lastCheckin).toLocaleString()}
              </p>
            </div>
          </div>
        ) : null}

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={vehicleOk}
              onChange={(e) => setVehicleOk(e.target.checked)}
              className="w-4 h-4 accent-nipost-green"
              aria-label="Vehicle condition OK"
              data-testid="vehicle-condition-ok"
            />
            <span className="text-sm text-gray-700">
              Vehicle is roadworthy and in good condition
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={fitOk}
              onChange={(e) => setFitOk(e.target.checked)}
              className="w-4 h-4 accent-nipost-green"
              aria-label="Fit to drive"
              data-testid="fit-to-drive-ok"
            />
            <span className="text-sm text-gray-700">
              I am fit to drive (no impairment, adequate rest)
            </span>
          </label>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="checkin-notes">
              Notes (optional)
            </label>
            <textarea
              id="checkin-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any concerns or observations…"
              rows={2}
              maxLength={1000}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-muted resize-none focus:outline-none focus:ring-1 focus:ring-nipost-green"
              data-testid="checkin-notes"
            />
          </div>

          {checkin.isError ? (
            <p className="text-xs text-destructive">
              Failed to record check-in —{' '}
              {checkin.error instanceof Error ? checkin.error.message : 'unknown error'}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => checkin.mutate()}
            disabled={checkin.isPending}
            data-testid="submit-checkin"
            className="inline-flex items-center gap-2 bg-nipost-green text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            {checkin.isPending ? 'Submitting…' : 'Submit check-in'}
          </button>
        </div>
      </div>

      {/* Headline KPIs — live · GET /v1/driver-portal/me/safety-stats. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="safety-kpis">
        <KPICard
          title="Accident-free streak"
          value={stats?.accidentFreeDays != null ? `${stats.accidentFreeDays}d` : '—'}
          hint="Days since last panic event"
          icon={Activity}
          tone={stats ? 'success' : 'warning'}
        />
        <KPICard
          title="Open cases"
          value={stats != null ? String(stats.openCases) : '—'}
          hint="ACTIVE + ACKNOWLEDGED panic_alert"
          icon={AlertTriangle}
          tone={stats && stats.openCases > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="SOS triggers (90d)"
          value={stats != null ? String(stats.sosTriggers90d) : '—'}
          hint="panic_alert in window"
          icon={ShieldAlert}
          tone={stats && stats.sosTriggers90d > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="Attestations due"
          value={stats != null ? String(stats.attestationsDue) : '—'}
          hint="training_completion expiring ≤30d"
          icon={FileCheck}
          tone={stats && stats.attestationsDue > 0 ? 'warning' : 'default'}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Cases — deferred */}
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" /> Incident cases
          </h3>
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-sm font-semibold text-gray-700">
              Incident case feed coming soon
            </p>
            <p className="text-xs text-muted mt-1">
              RTA, harassment and vehicle issues will render here per case.
            </p>
          </div>
        </div>

        {/* Attestations — deferred */}
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-nipost-green" /> Attestations
          </h3>
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
            <p className="text-xs font-semibold text-gray-700">
              Attestation tracker coming soon
            </p>
          </div>
          <p className="text-[11px] text-muted mt-3">
            Attestations gate clock-in. Your line manager is paged when one falls due.
          </p>
        </div>
      </div>

      {/* Distress checks — deferred */}
      <div className="bg-white border border-border rounded-xl p-5 mt-6">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-nipost-green" /> Distress checks
        </h3>
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
          <p className="text-sm font-semibold text-gray-700">
            Distress event log coming soon
          </p>
          <p className="text-xs text-muted mt-1">
            Off-route, stationary and geofence-flagged events with their
            resolutions will render here.
          </p>
        </div>
      </div>
    </div>
  );
}
